import React, { useState, useEffect, useRef } from "react";
import { GoogleMap, Marker, DirectionsRenderer, useLoadScript, OverlayView } from "@react-google-maps/api";
import { setDefaults, fromLatLng } from "react-geocode";
import "./styles.css";
import i18n from "../../../i18n";
import config from '../../../constants/settings';
import { mapOptions } from "./mapOptions";
import { TypeAddress } from "../../../constants/enums";
import scalesMarker from "../../../assets/images/markers/scales.png";
import destinyMarker from "../../../assets/images/markers/destiny.png";
import originMarker from "../../../assets/images/markers/origin.png";

export default function RoutingMap({
  selectedOrigin,
  selectedDestiny,
  setSelectedOrigin,
  setSelectedDestiny,
  setSelectedAddressDestiny,
  selectedAddress,
  titleAdressDestiny,
  setAddressesMarkers,
  addressesMarkers,
  setShowUnits,
  setShowAddresses,
  showAddresses,
  setNameTitleOrigin,
  setSelectedAddressOrigin,
  setNameTitleDestiny,
  titleAdressOrigin,
  setNewCreateAddressEmployee,
  rowsIndex,
  servicesView,
  typeAddress,
  typeService,
  setShowEmployees,
  setArrScales,
  arrScales,
  setFindEmployees,
  findEmployees,
  setOnFocusAddress,
  setSelectedAddress,
  titleAdressArrScales,
  titleAdressArrEmployees,
  setNameTitleArrScales,
  setNameTitleEmployees,
  selectedAddressOrigin,
  selectedAddressDestiny,
  onMarkerClick,
  recommendedRoutes,
  selectedRouteIndex,
  onRouteClick,
}) {

  const [libraries] = useState(['places']);
  const [oldAddress, setOldAddress] = useState({});
  const [selected, setSelected] = useState({});
  const [directions, setDirections] = useState(null);
  const [mapZoom, setMapZoom] = useState(10);
  const [isInitialCenter, setIsInitialCenter] = useState(true);
  const [center, setCenter] = useState({ lat: 19.432608, lng: -99.133209 });
  const [activeRoutes, setActiveRoutes] = useState([]);
  const mapRef = useRef(null);
  
  const routeColorsMap = [
    "#FF5F00",
    "#3B82F6", 
    "#10B981",
    "#8B5CF6",
    "#EF4444",
    "#F59E0B"
  ];

  const containerStyle = {
    height: '500px',
    width: '100%'
  };

  const defaultCenter = { lat: 19.432608, lng: -99.133209 };

  useEffect(() => {
    if (isInitialCenter && mapRef.current) {
      mapRef.current.panTo(defaultCenter);
      setIsInitialCenter(false);
    }
  }, [isInitialCenter]);

  useEffect(() => {
    if (selectedOrigin) {
      setCenter(selectedOrigin.location || selectedOrigin);
      setMapZoom(16);
      if (mapRef.current) {
        mapRef.current.panTo(selectedOrigin.location || selectedOrigin);
      }
    }
  }, [selectedOrigin]);

  useEffect(() => {
    if (selectedDestiny) {
      setCenter(selectedDestiny.location || selectedDestiny);
      setMapZoom(14);
      if (mapRef.current) {
        mapRef.current.panTo(selectedDestiny.location || selectedDestiny);
      }
    }
  }, [selectedDestiny]);

  useEffect(() => {
    let findAddress = addressesMarkers.filter((item) => {
      return item.typeAddress === TypeAddress.ARRAY_EMPLOYEES;
    }).shift();
    
    if (findAddress) {
      setSelected({ lng: findAddress.location.lng, lat: findAddress.location.lat });
    }
  }, [addressesMarkers]);

  useEffect(() => {
    if (selectedOrigin && selectedDestiny) {
      calculateRoute(selectedOrigin, selectedDestiny, arrScales);
    }
  }, [selectedOrigin, selectedDestiny, arrScales]);
  
  useEffect(() => {
    if (selectedOrigin && selectedDestiny && arrScales.length) {
      calculateRoute(selectedOrigin, selectedDestiny, arrScales);
    }
  }, [selectedOrigin, selectedDestiny, arrScales]);

  useEffect(() => {
    if (recommendedRoutes && recommendedRoutes.length > 0) {
      setActiveRoutes(recommendedRoutes);
    } else if (directions) {
      setActiveRoutes([{
        directions: directions,
        color: "#FF5F00",
        isSelected: true
      }]);
    } else {
      setActiveRoutes([]);
    }
  }, [recommendedRoutes, directions]);

  useEffect(() => {
    if (selectedOrigin && selectedDestiny) {
        calculateRoute(selectedOrigin, selectedDestiny, arrScales);
    } else {
        setDirections(null);
    }
}, [selectedOrigin, selectedDestiny, arrScales]);

useEffect(() => {
    if (recommendedRoutes && recommendedRoutes.length > 0) {
        setActiveRoutes(recommendedRoutes);
    } else if (directions) {
        setActiveRoutes([{
            directions: directions,
            color: "#FF5F00",
            isSelected: true
        }]);
    } else {
        setActiveRoutes([]);
        setDirections(null);
    }
}, [recommendedRoutes, directions]);

  setDefaults({
    key: config.google.secret,
    language: "es",
    region: "mx",
  });

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: config.google.secret,
    libraries: libraries,
    language: "es",
    region: "mx",
  });

  const calculateRoute = async (origin, destination, waypoints = []) => {
    if (!origin || !destination) {
      setDirections(null);
      return;
    }
  
    const directionsService = new window.google.maps.DirectionsService();
    
    const originLocation = origin.location 
      ? { lat: origin.location.lat, lng: origin.location.lng || origin.location.lon }
      : origin;
    
    const destinationLocation = destination.location 
      ? { lat: destination.location.lat, lng: destination.location.lng || destination.location.lon }
      : destination;
  
    try {
      const formattedWaypoints = waypoints
        .filter(scale => scale.location)
        .map((scale) => ({
          location: {
            lat: scale.location.lat,
            lng: scale.location.lng || scale.location.lon
          },
          stopover: true
        }));
  
      const request = {
        origin: originLocation,
        destination: destinationLocation,
        waypoints: formattedWaypoints,
        travelMode: window.google.maps.TravelMode.DRIVING,
        optimizeWaypoints: true,
        provideRouteAlternatives: false
      };
  
      const result = await directionsService.route(request);
      
      if (result.status === window.google.maps.DirectionsStatus.OK) {
        setDirections(result);
      } else {
        console.error("Error calculating route:", result.status);
        setDirections(null);
      }
    } catch (error) {
      console.error("Error calculating route:", error);
      setDirections(null);
    }
  };

  const getNameAddress = () => {
    let name = "";
    switch (typeAddress) {
      case TypeAddress.ARRAY_SCALES:
        name = i18n.t("Scale");
        break;
      case TypeAddress.ARRAY_EMPLOYEES:
        name = i18n.t("Employee");
        break;
      case TypeAddress.DESTINY:
        name = i18n.t("Destiny");
        break;
      case TypeAddress.ORIGIN:
        name = i18n.t("Origin");
        break;
    }
    return name;
  };

  const getMarkerIcon = (item) => {
    if (item.isService && item.isSelected) {
      return {
        url: 'data:image/svg+xml;base64,' + btoa(`
          <svg width="32" height="32" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
            <path d="M16 2C10.477 2 6 6.477 6 12C6 19 16 30 16 30C16 30 26 19 26 12C26 6.477 21.523 2 16 2Z" fill="#4CAF50"/>
            <circle cx="16" cy="12" r="4" fill="white"/>
          </svg>
        `),
        scaledSize: new window.google.maps.Size(32, 32),
        anchor: new window.google.maps.Point(16, 32)
      };
    }

    if (item.isService && !item.isSelected) {
      return {
        url: 'data:image/svg+xml;base64,' + btoa(`
          <svg width="32" height="32" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
            <path d="M16 2C10.477 2 6 6.477 6 12C6 19 16 30 16 30C16 30 26 19 26 12C26 6.477 21.523 2 16 2Z" fill="#9CA7C8"/>
            <circle cx="16" cy="12" r="4" fill="white"/>
          </svg>
        `),
        scaledSize: new window.google.maps.Size(32, 32),
        anchor: new window.google.maps.Point(16, 32)
      };
    }

    let iconUrl = "";
    switch (item.typeAddress) {
      case TypeAddress.ARRAY_SCALES:
        iconUrl = scalesMarker;
        break;
      case TypeAddress.ARRAY_EMPLOYEES:
        iconUrl = `marker-employee.png`;
        break;
      case TypeAddress.DESTINY:
        iconUrl = destinyMarker;
        break;
      case TypeAddress.ORIGIN:
        iconUrl = originMarker;
        break;
    }
    return iconUrl;
  };

  const getLabelMarker = (item) => {
    let name = "";
    let address = item?.description ?? ""; 
    switch (item.typeAddress) {
      case TypeAddress.ARRAY_SCALES:
        name = item.employeeId 
          ? i18n.t("EmployeeScale")
          : i18n.t("ScaleNumber", { number: item.id - 2 });
        break;
      case TypeAddress.ARRAY_EMPLOYEES:
        name = i18n.t("Employee");
        break;
      case TypeAddress.DESTINY:
        name = i18n.t("Destiny");
        break;
      case TypeAddress.ORIGIN:
        name = i18n.t("Origin");
        break;
    }

    if (item.isService) {
      name = item.isSelected ? i18n.t("SelectedService") : i18n.t("Service");
    }
    
    return (
      <div className="custom-label">
        <div className="marker-label-name">{name}</div>
        <div className="marker-label-address">{address}</div>
      </div>
    );
  };

  const handleMarkerClick = (item) => {
    if (onMarkerClick && item.isService) {
      onMarkerClick(item);
      return;
    }

    if (servicesView) {
      setSelectedAddress(item);
      setOnFocusAddress(item.typeAddress);
      setOldAddress(item);
      
      switch (item.typeAddress) {
        case TypeAddress.ORIGIN:
          setNameTitleOrigin(
            i18n.t("TitleAddress", {
              typeAddress: i18n.t("Origin"),
              address: item.description,
            })
          );
          break;
        case TypeAddress.DESTINY:
          setNameTitleDestiny(
            i18n.t("TitleAddress", {
              typeAddress: i18n.t("Destiny"),
              address: item.description,
            })
          );
          break;
        case TypeAddress.ARRAY_SCALES:
          setNameTitleArrScales(
            i18n.t("TitleAddress", {
              typeAddress: i18n.t("Scale"),
              address: item.description,
            })
          );
          break;
        case TypeAddress.ARRAY_EMPLOYEES:
          setNameTitleEmployees(
            i18n.t("TitleAddress", {
              typeAddress: i18n.t("Employee"),
              address: item.description,
            })
          );
          break;
        default:
          break;
      }
    }
  };

  const CustomMarker = ({ item, onMarkerDragEnd }) => {
    const markerPosition = item.location;
    const [isHovered, setIsHovered] = useState(false);
  
    const handleMouseEnter = () => setIsHovered(true);
    const handleMouseLeave = () => setIsHovered(false);
  
    const handleClick = () => {
      handleMarkerClick(item);
    };
  
    const shouldShowLabel = () => {
      if (item.isService) {
        return item.isSelected || isHovered;
      }

      return (
        item.typeAddress === TypeAddress.ORIGIN || 
        item.typeAddress === TypeAddress.DESTINY ||
        isHovered
      );
    };
  
    return (
      <>
        <Marker
          key={`${item.typeAddress}-${item.id}-${item.isSelected}`}
          icon={getMarkerIcon(item)}
          draggable={item.dragg}
          onDragEnd={(t, map, coord) => onMarkerDragEnd(t, item)}
          position={markerPosition}
          onMouseOver={handleMouseEnter}
          onMouseOut={handleMouseLeave}
          onClick={handleClick}
        />
        {shouldShowLabel() && (
          <OverlayView
            position={markerPosition}
            mapPaneName={OverlayView.FLOAT_PANE}
          >
            <div>{getLabelMarker(item)}</div>
          </OverlayView>
        )}
      </>
    );
  };

  const onMarkerDragEnd = (coord, address) => {
    const { latLng } = coord;
    const lat = latLng.lat();
    const lng = latLng.lng();
  
    fromLatLng(lat, lng)
      .then(({ results }) => {
        const newLocation = { lat, lng, lon: lng, long: lng };
        const formattedAddress = results[0].formatted_address;
  
        const updatedAddress = {
          ...address,
          location: newLocation,
          description: formattedAddress,
        };
  
        setAddressesMarkers((prevMarkers) =>
          prevMarkers.map((marker) =>
            marker.id === address.id ? updatedAddress : marker
          )
        );
  
        switch (address.typeAddress) {
          case TypeAddress.ORIGIN:
            setSelectedOrigin(updatedAddress);
            setSelectedAddressOrigin(updatedAddress);
            setNameTitleOrigin(
              i18n.t("TitleAddress", {
                typeAddress: getNameAddress(),
                address: formattedAddress,
              })
            );
            break;
  
          case TypeAddress.DESTINY:
            setSelectedDestiny(updatedAddress);
            setSelectedAddressDestiny(updatedAddress);
            setNameTitleDestiny(
              i18n.t("TitleAddress", {
                typeAddress: getNameAddress(),
                address: formattedAddress,
              })
            );
            break;
  
          case TypeAddress.ARRAY_SCALES:
            setArrScales((prevScales) =>
              prevScales.map((scale) =>
                scale.id === address.id ? updatedAddress : scale
              )
            );
            setNameTitleArrScales(
              i18n.t("TitleAddress", {
                typeAddress: getNameAddress(),
                address: formattedAddress,
              })
            );
            break;
  
          default:
            break;
        }
        
        setOnFocusAddress(address.typeAddress);
        calculateRoute(
          address.typeAddress === TypeAddress.ORIGIN ? updatedAddress : selectedOrigin,
          address.typeAddress === TypeAddress.DESTINY ? updatedAddress : selectedDestiny,
          address.typeAddress === TypeAddress.ARRAY_SCALES
            ? arrScales.map((scale) =>
                scale.id === address.id ? updatedAddress : scale
              )
            : arrScales
        );
      })
      .catch((error) => {
        console.error("Error - fromLatLng - ", error);
      });
  };

  if (!isLoaded) return <div>Loading...</div>;

  return (
    <>
      <div className={`places-container ${typeAddress === "array" ? 'col-12' : ''}`}>
        <GoogleMap
          zoom={mapZoom}
          center={center}
          mapContainerStyle={containerStyle}
          mapContainerClassName="map-container"
          options={mapOptions}
          onLoad={(map) => {
            mapRef.current = map;
          }}
        >
          {addressesMarkers.length !== 0 &&
            addressesMarkers
              .filter(marker => marker.location)
              .map((item) => (
                <CustomMarker
                  key={`${item.typeAddress}-${item.id}`}
                  item={{
                    ...item,
                    location: {
                      lat: item.location.lat,
                      lng: item.location.lng || item.location.lon
                    }
                  }}
                  onMarkerDragEnd={onMarkerDragEnd}
                />
              ))}
          
          {activeRoutes.map((route, index) => {
            if (!route || !route.directions || !route.directions.routes || route.directions.routes.length === 0) {
              return null;
            }
            
            return (
              <DirectionsRenderer
                key={`route-${index}-${route.color}`}
                directions={route.directions}
                options={{
                  polylineOptions: {
                    strokeColor: route.color || routeColorsMap[index % routeColorsMap.length] || "#FF5F00",
                    strokeWeight: route.isSelected ? 8 : 5,
                    strokeOpacity: route.isSelected ? 1 : 0.7,
                    zIndex: route.isSelected ? 1000 : index,
                  },
                  suppressMarkers: true,
                }}
                onClick={() => {
                  if (onRouteClick) {
                    onRouteClick(index);
                  }
                }}
                onMouseOver={() => {
                  if (mapRef.current) {
                    mapRef.current.setOptions({ draggableCursor: 'pointer' });
                  }
                }}
                onMouseOut={() => {
                  if (mapRef.current) {
                    mapRef.current.setOptions({ draggableCursor: '' });
                  }
                }}
              />
            );
          })}
        </GoogleMap>
      </div>
    </>
  );
}
